/**
 * @typedef {import('../../data/store').default} Store
 */

import {
    createStep,
    currentStep,
    addChildStep
} from '../../actions';

const cssClasses = {
    stepIndicatorSteps: 'step-indicator__steps',
};

export class StepIndicator {
    /**
     * Component that shows the active, completed and uncompleted steps.
     * @constructor
     * @param {Object} options
     * @param {Store} options.store
     */
    constructor(options) {
        this.store = options.store;
        this.translations = options.translations;
        this.state = this.store.getState();

        this.steps = [];
        this.$el = this.createStepInidicatorElement(this.state.steps);
        this.store.subscribe(this.update.bind(this));

        this.update();
    }

    /**
     * @param  {} _config={}
     */
    createStepInidicatorElement(_steps = {}) {
        this.stepIndicatorElem = document.createElement('ol');
        this.stepIndicatorElem.classList.add(cssClasses.stepIndicatorSteps);

        _steps.map(step => {
            const $step = this.createStepElem(step);
            if ($step) {
                this.stepIndicatorElem.appendChild($step);
            }

            return step;
        });

        return this.stepIndicatorElem;
    }

    createStepElem(_step) {
        this.store.dispatch(createStep(_step));

        for (const childStepId of _step.childStepIds) {
            this.store.dispatch(addChildStep(_step.id, childStepId));
        }

        if (_step.parentId || _step.hidden) {
            return false;
        }

        const name = this.translations[`progress_${_step.id}`] ? this.translations[`progress_${_step.id}`] : _step.name;
        const $stepEl = document.createElement('li');

        $stepEl.classList.add('step');
        $stepEl.setAttribute('data-id', _step.id);
        $stepEl.setAttribute('data-completed', _step.completed);
        $stepEl.setAttribute('data-active', _step.active);
        $stepEl.setAttribute('data-current', _step.current);
        $stepEl.tabIndex = 0;
        $stepEl.innerHTML = `
<button type='button' title='${__('Go to %s').replace(/%s/g,  name)}'>
<span class="step-indicator__step-name">${name}</span>
</button>
`;
        $stepEl.addEventListener('click', () => {
            const currentStep = this.state.steps.find(step => step.current === true); // eslint-disable-line no-shadow
            const stepClicked = this.state.steps.find(step => step.id === $stepEl.getAttribute('data-id'));
            const stepIdNumber = parseInt(stepClicked.id.substring(5, stepClicked.id.length), 0);

            if (stepClicked.completed) {
                this.gotoStep(stepIdNumber);
            } else if (stepClicked.id === 'step_6' && currentStep.id !== 'step_1') {
                const notCompleted = ['step_2', 'step_3', 'step_4', 'step_5'].filter(id => !this.getStep(id).valid);

                if (notCompleted.length) {
                    this.gotoStep(Number(notCompleted[0].substring(5, stepClicked.id.length)));
                } else {
                    this.gotoStep(6);
                }
            } else if (stepClicked.id === 'step_2' && currentStep.id === 'step_1') {
                this.gotoStep(2);
            }
        }, true);

        this.steps[_step.id] = $stepEl;

        return $stepEl;
    }

    completeStep(id, completed = false, progress) {
        this.steps[id].setAttribute('data-completed', completed);
        this.steps[id].setAttribute('data-progress', progress);
    }

    getStep(id) {
        for (const [key, step] of Object.entries(this.allSteps)) { // eslint-disable-line no-unused-vars
            if (step.id === id) {
                return step;
            }
        }

        return false;
    }

    currentStep(id, current = false) {
        this.steps[id].setAttribute('data-current', current);
    }

    activeStep(id, active = false) {
        this.steps[id].setAttribute('data-active', active);
    }

    update() {
        this.state = this.store.getState();
        this.allSteps = this.state.steps;

        for (const [key, step] of Object.entries(this.allSteps)) { // eslint-disable-line no-unused-vars
            // Key is nevere used.
            if (!step.parentId && !step.hidden === true) {
                const childStepsCompleted = this.getCompletedChilds(step);
                const stepProgress = this.getCompletedChildsProgress(step);

                if (childStepsCompleted) {
                    this.completeStep(step.id, step.completed, stepProgress);
                } else {
                    this.completeStep(step.id, false, stepProgress);
                }

                this.activeStep(step.id, step.active);
                this.currentStep(step.id, step.current);
            }
        };

        const current = this.getCurrentStep();
        if (current.hidden) {
            this.$el.classList.add('visually-hidden');
        }
    }

    getCompletedChilds(step) {
        return step.childStepIds.every((stepId) => {
            const stepIndex = this.allSteps.findIndex(_step => _step.id === stepId);

            return this.allSteps[stepIndex].completed;
        });
    }

    getCompletedChildsProgress(step) {
        let progressPercent = 0;
        const totalSteps = step.childStepIds.length + 1;

        for (const childStepId of [step.id, ...step.childStepIds]) {
            const stepIndex = this.allSteps.findIndex(_step => _step.id === childStepId);
            if (this.allSteps[stepIndex].completed) {
                progressPercent += 1;
            }
        }

        return `${totalSteps}_${progressPercent}`;
    }

    getCurrentStep() {
        this.state = this.store.getState();
        const current = this.state.steps.find(step => step.current === true);
        return current;
    }

    gotoStep(stepIdNumber) {
        let debounceTimeout;

        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(()=> {
            window.scrollTo(0, 85);
        }, 100);

        this.store.dispatch(currentStep(stepIdNumber));
    }

    render() {
        return this.$el;
    }
}

export default StepIndicator;
